/** Contextual "?" help — opens the SOP mapped to the current route in a side sheet. */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { HelpCircle, ArrowUpRight, BookOpen } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SopIcon, GROUP_TONE, SopBody } from "@/pages/knowledge/sopParts";

export default function ContextHelp({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get("/sop/context", { params: { path } }).then(({ data }) => setD(data)).catch(() => setD({ match: null, related: [] }));
  }, [open, path]);
  useEffect(() => {
    const onKey = (e) => { const el = document.activeElement; const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable); if (!typing && e.key === "?" && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setOpen(true); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);
  const go = (to) => { setOpen(false); navigate(to); };
  const m = d?.match;

  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Help for this screen" title="Help for this screen (?)" data-testid="os-context-help"
        className={"h-9 rounded-full grid place-items-center text-[color:var(--st-text-muted)] hover:text-[color:var(--st-accent)] hover:bg-[color:var(--st-accent-soft)] transition-colors " + (compact ? "w-9" : "w-9")}>
        <HelpCircle className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="context-help-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 text-left border-b border-[color:var(--st-border)]">
            <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[color:var(--st-text-muted)]">Help for this screen · <span className="font-mono normal-case tracking-normal">{path}</span></div>
            {m ? (
              <div className="flex items-start gap-3 mt-1">
                <span className={"h-10 w-10 shrink-0 rounded-xl grid place-items-center " + (GROUP_TONE[m.group] || GROUP_TONE.Home)}><SopIcon name={m.icon} className="h-5 w-5" /></span>
                <div className="min-w-0"><SheetTitle className="font-display text-lg leading-tight" data-testid="context-help-title">{m.title}</SheetTitle><SheetDescription className="text-xs mt-0.5">{m.summary}</SheetDescription></div>
              </div>
            ) : <><SheetTitle className="font-display text-lg">{d ? "No SOP mapped to this screen yet" : "Looking up…"}</SheetTitle><SheetDescription className="text-xs">Browse the SOP Centre for step-by-step instructions on every module.</SheetDescription></>}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4 os-scroll">
            {!d ? <div className="os-skeleton h-40 rounded-xl" /> : m ? <SopBody sop={m} compact path={path} onNavigate={() => setOpen(false)} /> : (
              <div className="os-card p-6 text-center text-[13px] text-[color:var(--st-text-muted)]" data-testid="context-help-empty">This screen has no SOP yet.{d.can_edit && " Admins can add one in the SOP Centre and map this route to it."}</div>
            )}
            {d?.related?.length > 0 && (
              <div className="mt-6 space-y-2"><div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[color:var(--st-text-muted)]">Related SOPs</div>
                {d.related.map((r) => <button key={r.key} onClick={() => go(`/sop/${r.key}`)} className="w-full flex items-center gap-2 os-card p-2.5 text-left text-[13px] hover:ring-1 hover:ring-[color:var(--st-accent)]" data-testid={`context-help-related-${r.key}`}><SopIcon name={r.icon} /> <span className="flex-1 truncate">{r.title}</span><ArrowUpRight className="h-3.5 w-3.5 text-[color:var(--st-text-subtle)]" /></button>)}
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-[color:var(--st-border)] flex items-center justify-between gap-2">
            {m ? <button onClick={() => go(`/sop/${m.key}`)} className="h-9 px-3 rounded-full bg-[color:var(--st-text)] text-white text-[12px] font-semibold inline-flex items-center gap-1.5" data-testid="context-help-open-full">Open full SOP <ArrowUpRight className="h-3.5 w-3.5" /></button> : <span />}
            <button onClick={() => go("/sop")} className="h-9 px-3 rounded-full border border-[color:var(--st-border)] bg-white text-[12px] font-medium inline-flex items-center gap-1.5" data-testid="context-help-centre"><BookOpen className="h-3.5 w-3.5" /> SOP Centre</button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
